import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { TranslocoModule } from '@jsverse/transloco';

import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { DialogBase } from '../../../interfaces';

@Component({
  selector: 'hmax-dialog-base',
  standalone: true,
  templateUrl: './dialog-base.component.html',
  styleUrls: ['./dialog-base.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, TranslocoModule, CapitalizePipe],
})
export class DialogBaseComponent implements OnInit {
  public data = inject<DialogBase>(MAT_DIALOG_DATA);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _dialogRef = inject(MatDialogRef<any>);
  private _sanitizer = inject(DomSanitizer);

  getSafeHtml(html: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * 🔴 O estado do botão de confirmar é lido por `computed`, e é o que faz a tela reagir.
   *
   * Medido em 2026-08-18 (o botão salvar do diálogo de integrações nunca liberava): esta casca recebe
   * `data` por INJEÇÃO, não por `@Input`, então mutar `data.enableSubmitButton` não suja a view dela —
   * e digitar no formulário suja o PAI, porque o campo é conteúdo projetado. Quem precisa ligar o
   * botão depois da abertura passa um `Signal`, e a leitura dele aqui dentro registra a dependência.
   *
   * ⚠️ Booleano mutado continua NÃO funcionando, de propósito: fingir que funciona exigiria abrir mão
   * do `OnPush` — e nem isso bastaria, porque desde o Angular 19 o `tick()` refresca só view suja.
   */
  readonly submitEnabled = computed<boolean>(() => {
    const flag = this.data.enableSubmitButton;
    return typeof flag === 'function' ? flag() : (flag ?? true);
  });

  ngOnInit(): void {
    this._dialogRef.addPanelClass('c_base-dialog');
  }

  handleConfirmClick(): void {
    if (this.data?.actions?.confirm?.action) {
      this.data.actions.confirm?.action();
    } else {
      this._dialogRef.close('confirm');
    }
  }

  handleRefreshClick(): void {
    if (this.data?.actions?.refresh?.action) {
      this.data.actions.refresh?.action();
    }
  }

  handleActionButtonClick(index?: number): void {
    if (typeof index !== 'number') {
      return;
    }

    if (this.data?.actions?.buttons?.[index]?.action) {
      this.data.actions.buttons[index].action?.();
    } else {
      this._dialogRef.close(this.data?.actions?.buttons?.[index]?.label);
    }
  }
}
